import React from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import MachineColumn from 'components/layout_and_quote_builder/MachineColumn.jsx';

// One card per ManufacturingLine (a machine configuration): for an offset_pair, a printing machine + a
// die-cutting machine; for a flexo_combined, a single combined printing_and_die_cutting_machine; for a
// die_cutting_only, a die-cutting machine and no press at all. Styled
// like the solution cards. Solutions for the manufacturingLine are searched once it is selected — onSelect.
// bare drops the card chrome (border, shadow, hover, click) so the line can sit INSIDE a Manufacturing Line
// List card as one of its lines, rather than as its own card -- the enclosing list card is the single bordered,
// selectable unit. widthStyle overrides the grid-derived 25% width for that in-list layout. hidePrintingColumn
// / hideDieCuttingColumn / hideCombinedColumn drop a machine column that another line in the same list already
// showed, so a machine the parts share (e.g. one die-cutter) is listed once -- one per machine the card can
// draw, so the rule holds whatever the part_type: an offset pair's two machines and a flexo line's single
// combined machine are all just machines. sectionLabels are the sub_sections the drawn machines make, badged
// together at the top: usually the line's own one, plus any whose line had every machine already drawn (it has
// no box left to badge). hidePricedSolutionCount says this page does not count
// priced solutions AT ALL (Frank's /mcc, which has no Solution Finder yet), which is different from a count
// that has not landed yet -- so the card shows neither the number nor the waiting spinner, instead of waiting
// forever on a fetch that will never come. All are omitted for a standalone card, so that rendering is
// unchanged.
const ManufacturingLineCard = ({ manufacturingLine, onSelect, maxMachineDimensionMm, sectionLabels = [], widthStyle, bare, hidePrintingColumn, hideDieCuttingColumn, hideCombinedColumn, hidePricedSolutionCount }) => {
  const machineColumns = () => {
    if (manufacturingLine.type === 'flexo_combined') {
      // One machine prints AND die-cuts, so it is a single column -- and a single machine, left EMPTY when
      // another line of the same list already drew it, exactly as the pair's columns are.
      if (hideCombinedColumn) {
        return <div className={styles.layout_column} />;
      }

      return (
        <MachineColumn
          label={`${t('layouts.layout_type_die_cutting')} + ${t('layouts.layout_type_printing')}`}
          machineName={manufacturingLine.printing_and_die_cutting_machine_label}
          machineTypeLabel={manufacturingLine.printing_machine_type_label}
          widthMm={manufacturingLine.printing_sheet_width_mm}
          heightMm={manufacturingLine.printing_sheet_height_mm}
          kind="combined"
          maxMachineDimensionMm={maxMachineDimensionMm}
        />
      );
    }

    if (manufacturingLine.type === 'die_cutting_only') {
      // NO PRESS AT ALL, so one column — a part_type that is never printed is made on a die-cutting machine
      // alone (hard cardboard, issue #5694). Drawn as two, the card would head an empty box "Baskı" and read
      // as a plan with a press missing rather than a plan with no press in it. The machine is the die-cutting
      // one, so it is the die-cutting column's hide flag that empties it when another line of the same list
      // already drew that cutter.
      if (hideDieCuttingColumn) {
        return <div className={styles.layout_column} />;
      }

      return (
        <MachineColumn
          label={t('layouts.layout_type_die_cutting')}
          machineName={manufacturingLine.die_cutting_machine_label}
          machineTypeLabel={manufacturingLine.die_cutting_machine_type_label}
          widthMm={manufacturingLine.sheet_width_mm}
          heightMm={manufacturingLine.sheet_height_mm}
          kind="die_cutting"
          maxMachineDimensionMm={maxMachineDimensionMm}
        />
      );
    }

    // Printing first, then die-cutting — the real production order, matching the solution cards. When a
    // machine already appears on another line of the same Manufacturing Line List (e.g. one die-cutter two
    // parts share), its column is left EMPTY here rather than removed, so the columns stay aligned across the
    // lines and the empty slot signals that machine is the shared one shown above.
    return (
      <>
        {hidePrintingColumn ? <div className={styles.layout_column} /> : (
          <MachineColumn label={t('layouts.layout_type_printing')} machineName={manufacturingLine.printing_machine_label} machineTypeLabel={manufacturingLine.printing_machine_type_label} widthMm={manufacturingLine.printing_sheet_width_mm} heightMm={manufacturingLine.printing_sheet_height_mm} kind="printing" maxMachineDimensionMm={maxMachineDimensionMm} />
        )}
        {hideDieCuttingColumn ? <div className={styles.layout_column} /> : (
          <MachineColumn label={t('layouts.layout_type_die_cutting')} machineName={manufacturingLine.die_cutting_machine_label} machineTypeLabel={manufacturingLine.die_cutting_machine_type_label} widthMm={manufacturingLine.sheet_width_mm} heightMm={manufacturingLine.sheet_height_mm} kind="die_cutting" maxMachineDimensionMm={maxMachineDimensionMm} />
        )}
      </>
    );
  };

  // Which of the section's chosen material sheets (e.g. B3/B2/B1) this ManufacturingLine can run. A
  // line is listed when it runs at least one of the chosen sheets, so without these chips two listed
  // lines would look interchangeable even when one runs every sheet and the other only the smallest.
  const materialDimensionChips = () => {
    const materialLayoutDimensions = manufacturingLine.material_layout_dimensions || [];
    if (materialLayoutDimensions.length === 0) {
      return null;
    }

    return (
      <div style={{ textAlign: 'center', marginTop: '10px' }}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
          {t('quote_builder.material_dimensions')}:
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px' }}>
          {materialLayoutDimensions.map((materialLayoutDimension) => (
            <span
              key={materialLayoutDimension.id}
              style={{
                fontSize: '12px', color: '#555', border: '1px solid #ccc', borderRadius: '4px',
                padding: '2px 8px', backgroundColor: '#f9f9f9'
              }}
            >
              {window.user_unit == 'in' ? materialLayoutDimension.description_in : materialLayoutDimension.description_mm}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const handleClick = () => {
    if (onSelect) {
      onSelect(manufacturingLine);
    }
  };

  // The count needs the section's shared solve, so it is fetched per card AFTER the listing renders
  // (see LayoutNavSectionCombinationList#fetchPricedSolutionCounts). Until it lands, priced_solution_count
  // is undefined and the card shows a small bottom-right spinner instead of a number. A page that counts no
  // priced solutions at all says so (hidePricedSolutionCount) and is never treated as still waiting.
  const hasPricedSolutionCount = typeof manufacturingLine.priced_solution_count === 'number';
  const waitingForPricedSolutionCount = !hasPricedSolutionCount && !hidePricedSolutionCount;

  // A single-part section has no sub_section label to badge and passes a blank one, so drop the blanks rather
  // than draw an empty pill.
  const badgedSectionLabels = sectionLabels.filter(Boolean);

  return (
    <div
      className={bare ? undefined : styles.combinations_grid_item_cont}
      data-manufacturing-line-identifier={manufacturingLine.identifier}
      onClick={bare ? undefined : handleClick}
      // Dim the card to 0.6 while its priced_solution_count is still being fetched (the spinner shows in the
      // bottom-right corner meanwhile); it returns to full opacity once the count lands.
      style={{ position: 'relative', opacity: waitingForPricedSolutionCount ? 0.6 : 1, transition: 'opacity 0.2s ease', ...(widthStyle || {}) }}
    >
      <div className={styles.combinations_grid_item}>
        {/* Which sub_section(s) (Cover / Tray / "Tray & Cover") the machines below make. The lines are already
            listed under a collapsible sub_section heading, but 35+ cards scroll well past it, so each card
            repeats its section as a badge -- a card read in isolation still shows where it belongs. Reuses
            the same green pill the solution cards use for their part names. Only multi-part sections carry a
            sub_section label; single-part sections pass none and show no badge.
            USUALLY one label. It is a list because a line whose machines are ALL already drawn above has no
            box of its own left, so its part is badged HERE, against the machines that make it, rather than
            left alone under an empty row. */}
        {badgedSectionLabels.length > 0 && (
          <div className={styles.part_badges_container} style={{ justifyContent: 'center', marginBottom: '8px' }}>
            {/* Keyed by position, not by the label: a label is not identity. Sub_sections of one section are
                uniquely labelled today, but LayoutNavBuilder#build_guest_section_for flat_maps sub_sections
                across every main_label into ONE section, so two could share a label -- and React would then
                drop a badge. The legacy listing keys its own badges composite for the same reason. */}
            {badgedSectionLabels.map((label, position) => (
              <span key={`${position}:${label}`} className={styles.efficiency_badge}>{label}</span>
            ))}
          </div>
        )}
        <div className={styles.layout_columns_container}>
          {machineColumns()}
        </div>
        {materialDimensionChips()}
        {/* How many of this line's solutions have a unit price (0 unless both machines are priced). */}
        {hasPricedSolutionCount && (
          <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px' }}>
            {t('quote_builder.priced_solutions')}: <strong>{manufacturingLine.priced_solution_count}</strong>
          </div>
        )}
        {waitingForPricedSolutionCount && (
          <div style={{ position: 'absolute', bottom: '8px', right: '8px' }} title={t('quote_builder.priced_solutions')}>
            <span className={styles.cardCountSpinner} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManufacturingLineCard;
